import React, { useEffect, useState, ReactNode } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ConfirmationModal } from '../../ui/Modal';

const MAX_COMMENT_LENGTH = 500;

export interface BlockActionConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (comment?: string) => void;
  itemName?: string;
  itemCode?: string;
  itemUniqueCode?: string;
  isLoading?: boolean;
  title: string;
  message: string;
  confirmText: string;
  loadingConfirmText: string;
  icon: ReactNode;
  commentPlaceholder: string;
}

const BlockActionConfirmationModal: React.FC<BlockActionConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  itemName,
  itemCode,
  itemUniqueCode,
  isLoading = false,
  title,
  message,
  confirmText,
  loadingConfirmText,
  icon,
  commentPlaceholder,
}) => {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!open) {
      setComment('');
    }
  }, [open]);

  const handleClose = () => {
    if (!isLoading) {
      setComment('');
      onClose();
    }
  };

  const handleConfirm = () => {
    const trimmed = comment.trim();
    onConfirm(trimmed || undefined);
  };

  const customContent = (
    <Box sx={{ textAlign: 'left' }}>
      {itemName != null && itemCode != null ? (
        <Box
          sx={(theme) => ({
            p: 2.5,
            borderRadius: '8px',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.08)
                : theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Typography
            variant="caption"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
              fontWeight: 500,
              display: 'block',
              mb: 1,
            })}
          >
            Pieza
          </Typography>
          <Typography
            variant="body1"
            sx={(theme) => ({
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1.5,
            })}
          >
            {itemName}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontSize: '0.8125rem',
              '& strong': {
                color: theme.palette.text.primary,
                fontWeight: 600,
              },
            })}
          >
            Código: <strong>{itemCode}</strong>
            {itemUniqueCode ? (
              <>
                {' '}
                · Único: <strong>{itemUniqueCode}</strong>
              </>
            ) : null}
          </Typography>
        </Box>
      ) : null}

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Comentario (opcional)"
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
        placeholder={commentPlaceholder}
        helperText={`${comment.length}/${MAX_COMMENT_LENGTH}`}
        sx={{ mt: 2 }}
        disabled={isLoading}
      />
    </Box>
  );

  return (
    <ConfirmationModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={title}
      message={message}
      confirmText={isLoading ? loadingConfirmText : confirmText}
      icon={icon}
      cancelText="Cancelar"
      variant="default"
      loading={isLoading}
      customContent={customContent}
    />
  );
};

export default BlockActionConfirmationModal;
